import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptRequest,
  cancelRequest,
  createInvite,
  deleteInvite,
  getFriendOfFriends,
  getFriendProfile,
  getInvite,
  getInviteProfile,
  getFriends,
  getRanking,
  getReceivedRequests,
  getSentRequests,
  rejectRequest,
  searchUsers,
  sendFriendRequest,
  unfriend,
} from '@/api/friends'
import type { Invite } from '@/api/friends'

const MY_INVITE_KEY = ['friends', 'invite', 'me'] as const
import type { RankingPeriod } from '@/api/friends'

export function useFriendList(offset = 0, limit = 10) {
  return useQuery({
    queryKey: ['friends', 'list', offset, limit],
    queryFn: () => getFriends(offset, limit),
  })
}

export function useRanking(period: RankingPeriod, offset = 0, limit = 10) {
  return useQuery({
    queryKey: ['friends', 'ranking', period, offset, limit],
    queryFn: () => getRanking(period, offset, limit),
  })
}

export function useReceivedRequests() {
  return useQuery({
    queryKey: ['friends', 'received'],
    queryFn: () => getReceivedRequests(),
  })
}

export function useSentRequests() {
  return useQuery({
    queryKey: ['friends', 'sent'],
    queryFn: () => getSentRequests(),
  })
}

export function useCancelRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cancelRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] })
    },
  })
}

export function useAcceptRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => acceptRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] })
    },
  })
}

export function useRejectRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => rejectRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends', 'received'] })
    },
  })
}

export function useSearchUsers(q: string, offset = 0, limit = 10) {
  return useQuery({
    queryKey: ['friends', 'search', q, offset, limit],
    queryFn: () => searchUsers(q, offset, limit),
    enabled: q.length > 0,
  })
}

export function useSendRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (receiverId: number) => sendFriendRequest(receiverId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] })
    },
  })
}

export function useFriendProfile(userId: number) {
  return useQuery({
    queryKey: ['friends', 'profile', userId],
    queryFn: () => getFriendProfile(userId),
    enabled: !Number.isNaN(userId),
  })
}

export function useInviteProfile(token: string) {
  return useQuery({
    queryKey: ['friends', 'inviteProfile', token],
    queryFn: () => getInviteProfile(token),
    enabled: token.length > 0,
  })
}

// 自分の招待リンク（GET）。発行・無効化の mutation 成功時にキャッシュを直接更新する。
export function useMyInvite(enabled = true) {
  return useQuery({
    queryKey: MY_INVITE_KEY,
    queryFn: getInvite,
    enabled,
  })
}

export function useIssueInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createInvite,
    onSuccess: (invite: Invite) => qc.setQueryData(MY_INVITE_KEY, invite),
  })
}

export function useRevokeInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteInvite,
    onSuccess: () => qc.setQueryData(MY_INVITE_KEY, null),
  })
}

export function useFriendOfFriends(userId: number, offset = 0, limit = 10) {
  return useQuery({
    queryKey: ['friends', 'fof', userId, offset, limit],
    queryFn: () => getFriendOfFriends(userId, offset, limit),
    enabled: !Number.isNaN(userId),
  })
}

export function useUnfriend() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => unfriend(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends'] })
    },
  })
}
